import get from 'lodash/get'
import last from 'lodash/last'

import type { ApiOrder, ApiOrderField } from '../api'
import { API_ORDER_DIRECTION, API_ORDERING_PARAM } from '../config/api'

/**
 * Returns ordering comparison
 * @param direction - Ordering direction
 * @param equal - Comparison with equality
 */
export function getComparison(
  direction: API_ORDER_DIRECTION | undefined | null,
  equal: boolean
): string {
  return `${
    direction ? (direction === API_ORDER_DIRECTION.DESC ? 'lt' : 'gt') : ''
  }${equal ? 'e' : ''}`
}

/**
 * Returns ordering filter param
 * @param field - Field name
 * @param comparison - Comparison
 */
export function getFilterParamName(field: string, comparison?: string): string {
  return `${field}${comparison ? `__${comparison}` : ''}`
}

/**
 * Returns order filter param name
 * @example
 * ```
 *
 *  getApiOrderFilterParamName(['name', API_ORDER_DIRECTION.DESC])
 *  // name__lt
 *
 *  getApiOrderFilterParamName(['name', API_ORDER_DIRECTION.ASC, true])
 *  // name__gte
 *
 *  getApiOrderFilterParamName(['name', API_ORDER_DIRECTION.ASC, true, 'param'])
 *  // param__gte
 *
 * ```
 *
 * @param field - API order field
 */
export function getApiOrderFilterParamName(
  field: ApiOrderField
): string | undefined {
  const [name, direction, equal = false, param = name] = field

  const splitParams = param?.split('.')
  const lastElement = last(splitParams)

  if (lastElement) {
    return getFilterParamName(lastElement, getComparison(direction, !!equal))
  }

  return
}

/**
 * Returns order filter params
 * @example
 * ```
 *
 *  getApiOrderFilterParams([['index', API_ORDER_DIRECTION.DESC]], { index: 1 })
 *  // index__lt=1
 *
 * ```
 *
 * @param order - API Order
 * @param item - Item to get param values
 */
export function getApiOrderFilterParams<Item = Record<string, unknown>>(
  order: ApiOrder<Record<string, unknown>>,
  item: Item
): Record<string, string> {
  return order.fields.reduce((params, field) => {
    const paramName = getApiOrderFilterParamName(field) as string

    if (paramName) {
      const paramValue = get(item, field[0])

      return paramValue ? { ...params, [paramName]: paramValue } : params
    } else {
      return params
    }
  }, {})
}

/**
 * Returns ordering param value
 * @example
 * ```
 *
 *  getOrderingValue([['index', API_ORDER_DIRECTION.DESC]])
 *  // -index
 *
 *  getOrderingValue([['index', API_ORDER_DIRECTION.DESC], ['date', API_ORDER_DIRECTION.ASC]])
 *  // -index,date
 *
 * ```
 *
 * @param fields - API order fields
 */
export function getOrderingValue<T>(fields: ApiOrderField<T>[]): string {
  return fields
    .map(
      ([field, direction]) =>
        `${direction === API_ORDER_DIRECTION.DESC ? direction : ''}${String(field)}`
    )
    .join(',')
}

/**
 * Returns ordering params
 * @example
 * ```
 *
 *  getOrderingParams({
 *    fields: [['index', API_ORDER_DIRECTION.DESC]],
 *    ordering: true
 *  })
 *  // { ordering: "-index" }
 *
 *  getOrderingParams({
 *    fields: [
 *      ['index', API_ORDER_DIRECTION.DESC],
 *      ['date', API_ORDER_DIRECTION.ASC]],
 *    ],
 *    ordering: true
 *  )
 *  // { ordering: "-index,date" }
 *
 * ```
 */
export function getOrderingParams<T>(
  order: ApiOrder<T>
): Record<string, string> | null {
  return order.ordering
    ? { [API_ORDERING_PARAM]: getOrderingValue<T>(order.fields) }
    : null
}

/**
 * Returns order params
 * @example
 * ```
 *
 *  getOrderParams({
 *    fields: [['index', API_ORDER_DIRECTION.DESC]], ordering: true
 *  }, { index: 5 })
 *  // { ordering: "-index", index__lt: 5 }
 *
 *  getOrderParams({
 *    fields: [['index', API_ORDER_DIRECTION.ASC, false, 'param']],
 *    ordering: true
 *  }, { index: 5 })
 *  // { ordering: "index", param__gt: 5 }
 *
 * ```
 *
 * @param order - API order
 * @param item - Item to get filter param values
 */
export function getOrderParams<Item = Record<string, unknown>>(
  order?: ApiOrder<Record<string, unknown>> | null,
  item?: Item | null
): Record<string, string> | null {
  return order
    ? {
        ...getOrderingParams(order),
        ...(item ? getApiOrderFilterParams(order, item) : null),
      }
    : null
}
